import type { Map as MapType } from "maplibre-gl";
import type { SkopaTarget, SkopaPlatform } from "../types"; // 🌟 ADDED: SiloPlatform type import
import type { Pdars, PdarPayload } from "../types/pdar";

type EventCallback = (data?: any) => void;

export interface MapEvents {
  // Map lifecycle
  "map:ready": MapType;
  "map:destroyed": void;

  "config:update": void;

  // Aircraft events
  "aircraft:clicked": { track_id: string; properties: any };
  "aircraft:selected": { track_id: string };
  "aircraft:deselected": { track_id: string | null };
  "aircraft:hovered": { track_id: string; properties: any };
  "aircraft:hover-end": void;
  "aircraft:selection-changed": {
    selectedTargets: string[];
  };
  "aircraft:selected-aircraft-updated": {
    aircraft: SkopaTarget[];
  };

  // Silo events
  "silo:clicked": { silo_id: string; properties: any };
  "silo:selected": { silo_id: string };
  "silo:deselected": { silo_id: string | null };
  "silo:hovered": { silo_id: string; properties: any };
  "silo:hover-end": void;
  // 🌟 ADDED: Type signature matching aircraft hook pattern updates
  "silos:selected-silos-updated": void;

  // Intercept events
  "intercept:clicked": { intercept_id: string; properties: any };
  "intercept:selected": { intercept_id: string };
  "intercept:deselected": { intercept_id: string | null };
  "intercept:status": {
    intercept_id: string;
    message: string;
    track_id: string;
    status: string;
  };
  "intercept:accepted": { intercept_id: number; track_id: string };
  "intercept:rejected": { intercept_id: number; track_id: string };

  // Pdar events
  "pdar:clicked": { pdar_id: string; properties: any };
  "pdar:selected": { pdar_id: string };
  "pdar:deselected": { pdar_id: string | null };
  "pdar:hovered": { pdar_id: string; properties: any };
  "pdar:hover-end": void;
  // Mirrors "aircraft:selected-aircraft-updated" — fired whenever the set of
  // selected PDAR sensors changes, carrying the resolved Pdars objects (not
  // just ids) so listeners (e.g. usePdarSelection) don't need a second lookup.
  "pdar:selected-pdar-updated": { pdars: Pdars[] };
  // Fired whenever a sensor's client-side orientation calibration offset
  // changes (see PdarsManager.setOrientationOffset). orientation_deg is the
  // resolved, already-normalized (0..360) value actually applied to that
  // sensor's icon rotation / AoA bearings — i.e. the wire `orientation` (or
  // 0 if the sensor hasn't been seen yet) plus the calibration offset, NOT
  // the raw offset itself — so listeners never need to re-derive it.
  "pdar:orientation-updated": { pdar_id: string; orientation_deg: number };

  // Data updates
  "data:aircraft-updated": { track_id: string; data: any };
  "data:silo-updated": { silo_id: string; data: any };
  "data:pdar-updated": { pdar_id: string; data: any };
  "data:batch-updated": { count: number };
  "data:aircraft-batch-updated": { targets: SkopaTarget[] };
  // 🌟 ADDED: Real-time telemetry batch update for Silo hook stream synchronization
  "data:silos-batch-updated": { platforms?: SkopaPlatform[] } | void;
  // Mirrors "data:aircraft-batch-updated" — fired whenever a pdar/pdar:batch
  // frame is merged into PdarsManager's caches, so hooks can re-sync their
  // "all pdars" list in real time without polling every render.
  "data:pdar-batch-updated": { payload: PdarPayload };

  // Layer events
  "layer:toggled": { layerId: string; visible: boolean };
  "layer:filtered": { layerId: string; filter: any };
}

export class MapEventBus {
  private events = new Map<keyof MapEvents, Set<EventCallback>>();

  on<K extends keyof MapEvents>(
    event: K,
    callback: (data: MapEvents[K]) => void,
  ): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(callback as EventCallback);
  }

  off<K extends keyof MapEvents>(
    event: K,
    callback: (data: MapEvents[K]) => void,
  ): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.delete(callback as EventCallback);
    }
  }

  emit<K extends keyof MapEvents>(event: K, data?: MapEvents[K]): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[MapEventBus] Error in ${event} callback:`, error);
        }
      });
    }
  }

  once<K extends keyof MapEvents>(
    event: K,
    callback: (data: MapEvents[K]) => void,
  ): void {
    const wrappedCallback = (data: MapEvents[K]) => {
      callback(data);
      this.off(event, wrappedCallback);
    };
    this.on(event, wrappedCallback);
  }

  clear(): void {
    this.events.clear();
  }

  debug(): void {
    console.log("[MapEventBus] Registered events:");
    this.events.forEach((callbacks, event) => {
      console.log(`  ${event}: ${callbacks.size} listeners`);
    });
  }
}
